/**
 * useConfirmDialog — Promise-returning replacement for `window.confirm()`.
 *
 * Drop-in for the 5 sites flagged in UX-1 (PR #95) plus anywhere else we
 * later want a properly-styled confirmation instead of the OS-native popup.
 * Renders via the existing shadcn `<AlertDialog>` so colours + dark mode +
 * keyboard handling come for free.
 *
 * Usage:
 *
 *   const { confirm, dialog } = useConfirmDialog();
 *   ...
 *   const ok = await confirm({
 *     title: "Move to bin?",
 *     description: "You can restore it from the bin within 30 days.",
 *     confirmLabel: "Move to bin",
 *     destructive: true,
 *   });
 *   if (!ok) return;
 *   // proceed
 *
 *   return (<>...{dialog}</>);
 */
import { useCallback, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const DEFAULT_CONFIG = {
  title: "Are you sure?",
  description: "",
  confirmLabel: "Continue",
  cancelLabel: "Cancel",
  destructive: false,
};

export function useConfirmDialog() {
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const resolverRef = useRef(null);

  const confirm = useCallback((opts = {}) => new Promise((resolve) => {
    setConfig({ ...DEFAULT_CONFIG, ...opts });
    resolverRef.current = resolve;
    setOpen(true);
  }), []);

  const handleAction = () => {
    setOpen(false);
    const fn = resolverRef.current;
    resolverRef.current = null;
    fn?.(true);
  };

  const handleCancel = () => {
    setOpen(false);
    const fn = resolverRef.current;
    resolverRef.current = null;
    fn?.(false);
  };

  const dialog = (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) handleCancel(); }}>
      <AlertDialogContent data-testid="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{config.title}</AlertDialogTitle>
          {config.description && <AlertDialogDescription className="whitespace-pre-line">{config.description}</AlertDialogDescription>}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel} data-testid="confirm-dialog-cancel">{config.cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleAction}
            className={config.destructive ? "bg-red-600 hover:bg-red-700 focus:ring-red-600" : "bg-[#021d49] hover:bg-[#052b66]"}
            data-testid="confirm-dialog-action"
          >
            {config.confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { confirm, dialog };
}
